import React, {SVGProps} from 'react';
import styled, {keyframes} from 'styled-components';
import {theme} from 'charts/components/style';
import {Coordinate} from 'charts/types';
import AvatarDefaultSvg from 'images/common/AnonymousUser.svg';
import {scales} from 'components/dashboard/manager/nine-box/NineBox.const';
import User from 'records/User';

interface AvatarPointProps extends SVGProps<SVGGElement> {
  mainRef: React.Ref<any>;
  coordinate: Coordinate;

  user?: User;
  image?: string;
  highlight?: boolean;
  disabled?: boolean;
  size?: number;
  hideAnimation?: boolean;
}

interface AvatarPointState {
  animate: boolean;
  imageFallback: boolean;
}

const colorPrepare = color => {
  if (!color) {
    return 'gray';
  }

  if (typeof color === 'string' && (color.length === 6 || color.length === 3)) {
    return `#${color}`;
  }

  return color;
};

const textFudge = 0.5;

/**
 * Given coordinates and a rep (and chart defaults), draw in x/y space their avatar with dropshadow,
 * degrading gracefully to initials and a default avatar if necessary
 */
export class AvatarPoint extends React.PureComponent<AvatarPointProps, AvatarPointState> {
  constructor(props) {
    super(props);

    this.state = {
      animate: false,
      imageFallback: false,
    };
  }

  static getDefinitions() {
    return (
      <g>
        <filter id="dropshadow" height="180%" width="170%" y="-35%" x="-35%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
          <feOffset dx="0" dy="2" result="offsetblur" />
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.8" />
          </feComponentTransfer>
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="desaturate">
          <feColorMatrix
            type="matrix"
            values="0.3333 0.3333 0.3333 0 0
                    0.3333 0.3333 0.3333 0 0
                    0.3333 0.3333 0.3333 0 0
                    0      0      0      1 0"
          />
        </filter>
      </g>
    );
  }

  startAnimation = evt => {
    if (this.props.onMouseEnter) {
      this.props.onMouseEnter(evt);
    }
    this.setState({animate: true});
  };

  stopAnimation = evt => {
    if (this.props.onMouseLeave) {
      this.props.onMouseLeave(evt);
    }
    this.setState({animate: false});
  };

  render() {
    const {
      coordinate,
      image,
      user,
      highlight,
      disabled,
      size = 20,
      mainRef,
      ...svgProps
    } = this.props;
    const {animate} = this.state;

    let innerSvg;
    if (!user && !image) {
      return null;
    }

    const color = colorPrepare(user?.color);
    const avatarUrl = user?.id ? `/api/assets/avatar/${user.id}` : undefined;
    if (image) {
      const imageSize = size * 1.3;
      innerSvg = (
        <image
          xlinkHref={image}
          x={scales.xScale(coordinate.x)! - imageSize / 2}
          y={scales.yScale(coordinate.y)! - imageSize / 2}
          height={imageSize}
          width={imageSize}
          filter={disabled ? 'url(#desaturate)' : undefined}
          onError={() => this.setState({imageFallback: true})}
        />
      );
    } else if (avatarUrl && !this.state.imageFallback) {
      innerSvg = (
        <image
          xlinkHref={avatarUrl}
          x={scales.xScale(coordinate.x)! - size / 2}
          y={scales.yScale(coordinate.y)! - size / 2}
          height={size}
          width={size}
          clipPath={'circle(45%)'}
          filter={disabled ? 'url(#desaturate)' : undefined}
          onError={() => this.setState({imageFallback: true})}
        />
      );
    } else if (user?.firstName && user?.lastName) {
      innerSvg = (
        <g>
          <circle
            r={size * 0.45}
            cx={scales.xScale(coordinate.x)}
            cy={scales.yScale(coordinate.y)}
            fill={disabled ? 'gray' : color}
          />
          <circle
            r={size * 0.4}
            cx={scales.xScale(coordinate.x)}
            cy={scales.yScale(coordinate.y)}
            fill={'white'}
          />
          <text
            x={scales.xScale(coordinate.x)}
            y={scales.yScale(coordinate.y)! + textFudge}
            fontFamily={theme.fonts.primaryFontFamily}
            fontSize={size * 0.4}
            fill={disabled ? 'gray' : color}
            fontWeight={'bold'}
            textAnchor={'middle'}
            alignmentBaseline={'middle'}>
            {user?.firstName.charAt(0).toUpperCase()}
            {user?.lastName.charAt(0).toUpperCase()}
          </text>
        </g>
      );
    } else {
      innerSvg = (
        <image
          xlinkHref={AvatarDefaultSvg}
          x={scales.xScale(coordinate.x)! - size / 2}
          y={scales.yScale(coordinate.y)! - size / 2}
          height={size}
          width={size}
          filter={disabled ? 'url(#desaturate)' : undefined}
        />
      );
    }

    return (
      <GroweyGroup style={{}} ref={mainRef}>
        {animate &&
          !disabled &&
          (highlight ? (
            <GreenRadarPulse
              r={size}
              cx={scales.xScale(coordinate.x)}
              cy={scales.yScale(coordinate.y)}
            />
          ) : (
            <GrayRadarPulse
              r={size}
              cx={scales.xScale(coordinate.x)}
              cy={scales.yScale(coordinate.y)}
            />
          ))}
        <g
          {...svgProps}
          data-id={user?.id ?? image}
          onMouseEnter={this.startAnimation}
          onMouseLeave={this.stopAnimation}>
          {highlight && !disabled && (
            <circle
              r={size * 0.625}
              cx={scales.xScale(coordinate.x)}
              cy={scales.yScale(coordinate.y)}
              fill={theme.colors.primaryActiveHighlight}
            />
          )}
          <circle
            r={size * 0.525}
            cx={scales.xScale(coordinate.x)}
            cy={scales.yScale(coordinate.y)}
            fill="white"
            style={{filter: 'url(#dropshadow)'}}
          />
          {innerSvg}
          {disabled && (
            <circle
              r={size / 2}
              cx={scales.xScale(coordinate.x)}
              cy={scales.yScale(coordinate.y)}
              fill={'black'}
              opacity={'0.25'}
            />
          )}
        </g>
      </GroweyGroup>
    );
  }
}

const growOut = keyframes`
  from {
    transform: scale(0.2);
  }
  to {
    transform: scale(1);
  }
`;
const GroweyGroup = styled.g`
  animation: ${growOut} 1 0.5s ease-in-out;
  cursor: pointer;
  transform-origin: center center;
`;

const pulseGreen = keyframes`
  0% {
    r: 2%;
    fill: green;
    opacity: .8;
  }
  75% {
    r: 6%;
    fill: #00B700;
    opacity: .3;
  }
  100% {
    r: 7%;
    fill: #00D700;
    opacity: 0;
  }
`;

const pulseGray = keyframes`
  0% {
    r: 2%;
    fill: #505050;
    opacity: .8;
  }
  75% {
    r: 6%;
    fill: #808080;
    opacity: .3;
  }
  100% {
    r: 7%;
    fill: #e4e4e4;
    opacity: 0;
  }
`;

const GreenRadarPulse = styled.circle`
  opacity: 0;
  animation: ${pulseGreen} 1 1s ease-out;
  pointer-events: none;
`;

const GrayRadarPulse = styled.circle`
  opacity: 0;
  animation: ${pulseGray} 1 1s ease-out;
  pointer-events: none;
`;
